import { create } from "zustand";
import { CartItem } from "../types/user";
import { Cart, AddToCartPayload } from "../types/cart";
import { generateCartItemId, saveAnonymousCart, loadAnonymousCart, createEmptyCart } from "../utils/cart-storage";

interface CartStore extends Cart {
  addItem: (payload: AddToCartPayload) => string;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateVariants: (itemId: string, variants: CartItem["variants"]) => void;
  clearCart: () => void;
  setCart: (cart: Cart) => void;
  revertAddItem: (itemId: string) => void;
  revertRemoveItem: (item: CartItem) => void;
  revertUpdateQuantity: (itemId: string, previousQuantity: number) => void;
  revertUpdateVariants: (itemId: string, previousVariants: CartItem["variants"]) => void;
  getItemById: (itemId: string) => CartItem | undefined;
  hasItem: (itemId: string) => boolean;
  syncToStorage: () => void;
  loadFromStorage: () => void;
}

export const useCartStore = create<CartStore>((set, get) => ({
  ...createEmptyCart(),

  addItem: (payload: AddToCartPayload) => {
    const itemId = generateCartItemId(payload.productId, payload.variants);

    set((state) => {
      const existingItemIndex = state.items.findIndex((item) => item.id === itemId);

      let newItems: CartItem[];

      if (existingItemIndex >= 0) {
        newItems = state.items.map((item, index) =>
          index === existingItemIndex ? { ...item, quantity: item.quantity + payload.quantity } : item
        );
      } else {
        const newItem: CartItem = {
          id: itemId,
          productId: payload.productId,
          name: payload.name,
          price: payload.price,
          quantity: payload.quantity,
          image: payload.image,
          tags: Object.values(payload.variants).filter(Boolean) as string[],
          variants: payload.variants,
          stock: payload.stock,
        };
        newItems = [...state.items, newItem];
      }

      const newState = {
        ...state,
        items: newItems,
        totalItems: newItems.reduce((sum, item) => sum + item.quantity, 0),
        totalPrice: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
      };

      if (!newState.userId) {
        saveAnonymousCart(newState);
      }

      return newState;
    });

    return itemId;
  },

  removeItem: (itemId: string) => {
    set((state) => {
      const newItems = state.items.filter((item) => item.id !== itemId);

      const newState = {
        ...state,
        items: newItems,
        totalItems: newItems.reduce((sum, item) => sum + item.quantity, 0),
        totalPrice: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
      };

      if (!newState.userId) {
        saveAnonymousCart(newState);
      }

      return newState;
    });
  },

  updateQuantity: (itemId: string, quantity: number) => {
    set((state) => {
      const newItems = state.items.map((item) => (item.id === itemId ? { ...item, quantity } : item));

      const newState = {
        ...state,
        items: newItems,
        totalItems: newItems.reduce((sum, item) => sum + item.quantity, 0),
        totalPrice: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
      };

      if (!newState.userId) {
        saveAnonymousCart(newState);
      }

      return newState;
    });
  },

  updateVariants: (itemId: string, variants: CartItem["variants"]) => {
    set((state) => {
      const newItems = state.items.map((item) =>
        item.id === itemId
          ? {
              ...item,
              variants,
              tags: Object.values(variants).filter(Boolean) as string[],
            }
          : item
      );

      const newState = {
        ...state,
        items: newItems,
      };

      if (!newState.userId) {
        saveAnonymousCart(newState);
      }

      return newState;
    });
  },

  clearCart: () => {
    const state = get();
    const newState = createEmptyCart(state.userId);

    set(newState);

    if (!newState.userId) {
      saveAnonymousCart(newState);
    }
  },

  setCart: (cart: Cart) => {
    set(cart);

    if (!cart.userId) {
      saveAnonymousCart(cart);
    }
  },

  revertAddItem: (itemId: string) => {
    set((state) => {
      const item = state.items.find((i) => i.id === itemId);
      if (!item) return state;

      let newItems: CartItem[];

      if (item.quantity > 1) {
        newItems = state.items.map((i) => (i.id === itemId ? { ...i, quantity: i.quantity - 1 } : i));
      } else {
        newItems = state.items.filter((i) => i.id !== itemId);
      }

      const newState = {
        ...state,
        items: newItems,
        totalItems: newItems.reduce((sum, item) => sum + item.quantity, 0),
        totalPrice: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
      };

      if (!newState.userId) {
        saveAnonymousCart(newState);
      }

      return newState;
    });
  },

  revertRemoveItem: (item: CartItem) => {
    set((state) => {
      const newItems = [...state.items, item];

      const newState = {
        ...state,
        items: newItems,
        totalItems: newItems.reduce((sum, item) => sum + item.quantity, 0),
        totalPrice: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
      };

      if (!newState.userId) {
        saveAnonymousCart(newState);
      }

      return newState;
    });
  },

  revertUpdateQuantity: (itemId: string, previousQuantity: number) => {
    set((state) => {
      const newItems = state.items.map((item) => (item.id === itemId ? { ...item, quantity: previousQuantity } : item));

      const newState = {
        ...state,
        items: newItems,
        totalItems: newItems.reduce((sum, item) => sum + item.quantity, 0),
        totalPrice: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
      };

      if (!newState.userId) {
        saveAnonymousCart(newState);
      }

      return newState;
    });
  },

  revertUpdateVariants: (itemId: string, previousVariants: CartItem["variants"]) => {
    set((state) => {
      const newItems = state.items.map((item) =>
        item.id === itemId
          ? {
              ...item,
              variants: previousVariants,
              tags: Object.values(previousVariants).filter(Boolean) as string[],
            }
          : item
      );

      const newState = {
        ...state,
        items: newItems,
      };

      if (!newState.userId) {
        saveAnonymousCart(newState);
      }

      return newState;
    });
  },

  getItemById: (itemId: string) => {
    return get().items.find((item) => item.id === itemId);
  },

  hasItem: (itemId: string) => {
    return get().items.some((item) => item.id === itemId);
  },

  syncToStorage: () => {
    const state = get();
    if (!state.userId) {
      saveAnonymousCart(state);
    }
  },

  loadFromStorage: () => {
    const cart = loadAnonymousCart();
    set(cart);
  },
}));
